/**
 * Auth Context
 * Provides authentication state and methods to the entire app.
 * 
 * Usage:
 *   Wrap your app with <AuthProvider>
 *   Then in any component: const { user, isAuthenticated, login, logout } = useAuth();
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import {
  login as authLogin,
  setPassword as authSetPassword,
  changePassword as authChangePassword,
  resetPassword as authResetPassword,
  getActiveUsers as authGetActiveUsers,
  getSession,
  logout as authLogout,
  getCurrentUser,
} from "../services/auth";

// Create the context
const AuthContext = createContext(null);

/**
 * AuthProvider component - wraps the app to provide auth state
 */
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true); // Loading while checking session
  const [activeUsers, setActiveUsers] = useState([]); // For PIC dropdown

  // Check for existing session on mount
  useEffect(() => {
    const checkSession = () => {
      try {
        const currentUser = getCurrentUser();
        if (currentUser) {
          setUser(currentUser);
          setIsAuthenticated(true);
        }
      } catch (error) {
        console.error("Error checking session:", error);
      } finally {
        setIsLoading(false);
      }
    };

    checkSession();
  }, []);

  // Fetch active users when authenticated (for PIC dropdown)
  useEffect(() => {
    if (isAuthenticated) {
      fetchActiveUsers();
    }
  }, [isAuthenticated]);

  /**
   * Login handler
   * @param {string} username
   * @param {string} password
   * @param {boolean} rememberMe
   * @returns {Promise<Object>} - User data
   */
  const login = useCallback(async (username, password, rememberMe = false) => {
    const userData = await authLogin(username, password, rememberMe);
    setUser(userData);
    setIsAuthenticated(true);
    return userData;
  }, []);

  /**
   * Logout handler
   */
  const logout = useCallback(() => {
    authLogout();
    setUser(null);
    setIsAuthenticated(false);
    setActiveUsers([]);
  }, []);

  /**
   * Set password handler (first-time setup)
   * @param {string} username
   * @param {string} newPassword
   * @param {string} confirmPassword
   * @returns {Promise<Object>}
   */
  const setPassword = useCallback(async (username, newPassword, confirmPassword) => {
    return await authSetPassword(username, newPassword, confirmPassword);
  }, []);

  /**
   * Change password handler (logged-in user)
   * @param {string} currentPassword
   * @param {string} newPassword
   * @param {string} confirmPassword
   * @returns {Promise<Object>}
   */
  const changePassword = useCallback(async (currentPassword, newPassword, confirmPassword) => {
    if (!user) throw new Error("Not logged in");
    return await authChangePassword(user.username, currentPassword, newPassword, confirmPassword);
  }, [user]);

  /**
   * Reset password handler
   * @param {string} username
   * @param {string} resetCode
   * @param {string} newPassword
   * @param {string} confirmPassword
   * @returns {Promise<Object>}
   */
  const resetPassword = useCallback(async (username, resetCode, newPassword, confirmPassword) => {
    return await authResetPassword(username, resetCode, newPassword, confirmPassword);
  }, []);

  /**
   * Fetch active users from the backend (for PIC dropdown)
   */
  const fetchActiveUsers = useCallback(async () => {
    try {
      const users = await authGetActiveUsers();
      setActiveUsers(users);
    } catch (error) {
      console.error("Error fetching active users:", error);
    }
  }, []);

  /**
   * Get PIC options for dropdowns
   * Returns array of display names from active users
   * @returns {Array<string>}
   */
  const getPicOptions = useCallback(() => {
    return activeUsers.map((u) => u.displayName).sort();
  }, [activeUsers]);

  // Context value - everything components need access to
  const value = {
    // State
    user,
    isAuthenticated,
    isLoading,
    activeUsers,

    // Auth actions
    login,
    logout,
    setPassword,
    changePassword,
    resetPassword,

    // User helpers
    getPicOptions,
    fetchActiveUsers,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

/**
 * useAuth hook - access auth context from any component
 * 
 * Usage:
 *   const { user, isAuthenticated, login, logout } = useAuth();
 */
export const useAuth = () => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }

  return context;
};

export default AuthContext;
